import React, { ReactNode, useCallback, useState, useMemo } from 'react';
import { navigate } from 'gatsby';
import { useLocation } from '@reach/router';

import { THEMES } from './Theme.config';
import { ThemeType, Theme } from './Theme.model';

interface ThemeContextProps {
  themeType: ThemeType;
  theme: Theme;
  saveTheme: (themePrefix: string, url: string) => void;
}

interface Props {
  children: ReactNode;
}

export const ThemeContext = React.createContext({
  themeType: 'enTheme',
  theme: THEMES.enTheme,
} as ThemeContextProps);

const createUrl = (location: string, url: string): string => {
  return `${location !== 'en' ? `/${location}` : ''}${!url.length ? '/' : `/${url}`}`;
};

export const ThemeProvider = ({ children }: Props) => {
  const location = useLocation();
  const currentTheme = (location.pathname.includes('/uz') ? ThemeType.uzTheme : ThemeType.enTheme) as ThemeType;
  const [theme, setTheme] = useState<ThemeType>(currentTheme);

  const update = useCallback((themePrefix: string, url: string) => {
    localStorage.setItem('location', themePrefix);
    setTheme(`${themePrefix}Theme` as ThemeType);
    navigate(createUrl(themePrefix, url), { replace: true });
  }, []);

  const themeProviderValue = useMemo(
    () => ({
      themeType: theme,
      theme: THEMES[theme],
      saveTheme: update,
    }),
    [theme, update],
  );

  return <ThemeContext.Provider value={themeProviderValue}>{children}</ThemeContext.Provider>;
};

export const useTheme = () => React.useContext(ThemeContext);
